import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { EventService } from '../../services/event.service';
import { AttendeeService } from '../../services/attendee.service';
import { CurrentEventService } from '../../services/current-event.service';
import { GenericEvent } from '../../models/event.model';
import { RegistrationModalComponent } from '../registration-modal/registration-modal';
import { Subject, takeUntil, finalize } from 'rxjs';

@Component({
  selector: 'app-event-detail',
  imports: [CommonModule, FormsModule, RegistrationModalComponent],
  templateUrl: './event-detail.html',
  styleUrl: './event-detail.css'
})
export class EventDetailComponent implements OnInit, OnDestroy {
  event: GenericEvent | null = null;
  loading = false;
  error: string | null = null;
  declined = false;
  declining = false;
  showModal = false;
  ignoredAttendee = false;
  showEventCodeForm = false;
  eventCode = '';
  searchError: string | null = null;
  searching = false;
  mapImageFailed = false;
  switchingAttendee = false;

  private destroy$ = new Subject<void>();
  private token: string | null = null;

  constructor(
    private eventService: EventService,
    private attendeeService: AttendeeService,
    private currentEventService: CurrentEventService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.token = params['token'] || null;

      if (!this.token) {
        this.showEventCodeForm = true;
        this.event = null;
        return;
      }

      this.showEventCodeForm = false;
      this.ignoredAttendee = false;
      this.declined = false;
      this.error = null;
      this.loading = true;

      this.eventService.decodeToken(this.token).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (event) => {
          this.event = event;
          this.mapImageFailed = false;
          this.currentEventService.set(event);
        },
        error: (err) => {
          console.error('Error decoding token:', err);
          this.error = 'This invitation link is invalid or has expired. Please contact your representative.';
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openRegistration() {
    this.showModal = true;
  }

  closeRegistration() {
    this.showModal = false;
  }

  onRegistrationSuccess() {
    this.showModal = false;
    this.router.navigate(['/thank-you'], {
      queryParams: { token: this.token }
    });
  }

  decline() {
    if (!this.event || !this.token || this.declining) return;
    const attendeeId = this.event.attendeeId ?? this.event.attendee?.id;
    if (!attendeeId) {
      this.error = 'Unable to decline: invitation details are missing.';
      this.cdr.detectChanges();
      return;
    }

    this.declining = true;
    this.error = null;
    this.attendeeService.declineInvitation(
      this.event.companyId,
      this.event.id,
      attendeeId,
      this.token
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.declining = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: () => {
        this.declined = true;
        if (this.event) {
          this.event = {
            ...this.event,
            invitedStatusId: 'declined',
          };
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.error = err.error?.error?.message || err.error?.message || 'Unable to decline this invitation. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  undoDecline() {
    this.declined = false;
  }

  onSubmitEventCode() {
    if (!this.eventCode.trim()) return;

    this.searchError = null;
    this.searching = true;

    this.eventService.searchEventByCode(this.eventCode).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.searching = false;
        if (result?.token) {
          this.router.navigate([], {
            queryParams: { type: 'attendee_register', token: result.token },
            replaceUrl: true
          });
        } else {
          this.searchError = `No event found with code "${this.eventCode}". Please check the code and try again.`;
        }
      },
      error: (err) => {
        this.searching = false;
        const apiMessage = err.error?.error?.message || err.error?.message;
        if (err.status === 404) {
          this.searchError = apiMessage || `No event found with code "${this.eventCode}". Please check the code and try again.`;
        } else {
          this.searchError = apiMessage || 'An error occurred while searching. Please try again.';
        }
        this.cdr.detectChanges();
      }
    });
  }

  /** Event is canceled (status_id 5 is the typical "canceled" status) */
  get isCanceled(): boolean {
    return this.event?.eventStatusId === 5;
  }

  /** Event is in the "confirmed" status — only confirmed events accept new registrations. */
  get isConfirmed(): boolean {
    return String(this.event?.eventStatusId ?? '').toLowerCase() === 'confirmed';
  }

  /** Attendee has declined the invitation — invited_status_id takes priority over everything */
  get isDeclinedStatus(): boolean {
    const invitedStatus = (this.event?.invitedStatusId ?? '').toLowerCase();
    return invitedStatus === 'declined' || this.declined;
  }

  /** Attendee is registered or host-confirmed — hide RSVP card entirely.
   *  Only applies when invited_status_id is NOT declined. */
  get isAttending(): boolean {
    if (this.isDeclinedStatus) return false;
    const statusId = String(this.event?.attendeeStatusId ?? '').toLowerCase();
    const statusLabel = (this.event?.attendeeStatusLabel ?? '').toLowerCase();
    const attendingStatuses = ['registered', 'host_confirmed', 'host confirmed', 'confirmed'];
    return attendingStatuses.includes(statusId) || attendingStatuses.includes(statusLabel);
  }

  /** Whether to show the RSVP accept/decline card */
  get showRsvpCard(): boolean {
    return !this.isCanceled && !this.isDeclinedStatus && !this.isAttending;
  }

  /** Pill badge config: text and CSS class */
  get statusPill(): { text: string; cssClass: string } | null {
    if (this.isCanceled) {
      return { text: 'Canceled', cssClass: 'pill-red' };
    }
    if (this.isDeclinedStatus) {
      return { text: 'Not Attending', cssClass: 'pill-neutral' };
    }
    if (this.isAttending) {
      return { text: 'Attending', cssClass: 'pill-green' };
    }
    return null;
  }

  ignoreAttendee() {
    if (!this.token || this.switchingAttendee) return;

    this.switchingAttendee = true;
    this.error = null;
    this.eventService.getEventOnlyToken(this.token).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.switchingAttendee = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (result) => {
        // Reload onto an event-only register token (no attendeeId) so a new
        // registration creates a new attendee instead of overwriting the invitee.
        this.router.navigate([], {
          queryParams: { type: 'attendee_register', token: result.token },
          replaceUrl: true
        });
      },
      error: (err) => {
        this.error = err.error?.error?.message || err.error?.message
          || 'Unable to open a generic registration link. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  get eventForModal(): GenericEvent | null {
    if (!this.event) return null;
    if (this.ignoredAttendee) return { ...this.event, attendee: undefined, attendeeId: undefined };
    return this.event;
  }

  get attendeeLabel(): string {
    if (!this.event?.attendee || this.ignoredAttendee) return '';
    const att = this.event.attendee;
    const name = [att.first_name, att.last_name].filter(Boolean).join(' ');
    const degreesArr = Array.isArray(att.degrees) ? att.degrees : (att.degrees ? [att.degrees] : []);
    const degrees = degreesArr.filter(Boolean).join(', ');
    return degrees ? `${name}, ${degrees}` : name;
  }

  formatTimezone(): string {
    if (!this.event?.timezone) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this.event.timezone,
        timeZoneName: 'short',
      }).formatToParts(new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value || this.event.timezone;
    } catch {
      return this.event.timezone;
    }
  }

  formatVenueAddress(): string {
    if (!this.event?.venue) return '';
    const parts = [
      this.event.venue.address1,
      this.event.venue.city,
      this.event.venue.state
    ].filter(Boolean);
    if (this.event.venue.zip) {
      return parts.join(', ') + ' ' + this.event.venue.zip;
    }
    return parts.join(', ');
  }

  getStaticMapUrl(): string | null {
    if (!this.event?.venue?.latitude || !this.event?.venue?.longitude) return null;
    const { latitude: lat, longitude: lng } = this.event.venue;
    const zoom = 15;
    const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
    const rad = lat * Math.PI / 180;
    const y = Math.floor((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, zoom));
    return `https://a.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${x}/${y}@2x.png`;
  }

  getMapPinPosition(): { left: string; top: string } | null {
    if (!this.event?.venue?.latitude || !this.event?.venue?.longitude) return null;
    const { latitude: lat, longitude: lng } = this.event.venue;
    const zoom = 15;
    const xFrac = (lng + 180) / 360 * Math.pow(2, zoom);
    const rad = lat * Math.PI / 180;
    const yFrac = (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, zoom);
    return {
      left: `${(xFrac - Math.floor(xFrac)) * 100}%`,
      top: `${(yFrac - Math.floor(yFrac)) * 100}%`
    };
  }

  // Shifts the image so the pin coordinate lands at the vertical center of the 200px container.
  // translateY(calc(100px - pinTop%)) works because % in translateY is relative to the element's
  // own height, which equals the container width (the tile is square, rendered at width: 100%).
  getMapImageTransform(): string {
    const pos = this.getMapPinPosition();
    if (!pos) return '';
    return `translateY(calc(100px - ${pos.top}))`;
  }

  getGoogleMapsUrl(): string | null {
    if (!this.event?.venue) return null;
    const v = this.event.venue;
    if (v.googleMapsUri) return v.googleMapsUri;
    if (v.latitude && v.longitude) {
      return `https://www.google.com/maps?q=${v.latitude},${v.longitude}`;
    }
    if (v.address1 && v.city && v.state) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${v.address1}, ${v.city}, ${v.state} ${v.zip || ''}`.trim())}`;
    }
    return null;
  }
}
